import { useCallback, useEffect, useState } from 'react';

import closeIcon from '@/library/assets/svg/actions/close.svg';
import downloadIcon from '@/library/assets/svg/actions/download.svg';
import rotateIcon from '@/library/assets/svg/actions/rotate.svg';
import SvgRender from '@/library/components/general/SvgRender';
import GalleryReplaceImageButton from '@/library/components/gallery/components/GalleryReplaceImageButton.tsx';

import { Attachment } from '@/library/types/files';
import { FC } from 'react';
import { rotateDirectionEnums } from './helpers';
import { fileSize, getFileType } from '@/library/utils/files';
import { handleFileDownload } from '@/library/utils/download';
import { RotateDirectionType } from '../store/types';
import { isFileSynced } from '@/api/files/api';

type GalleryHeaderProps = {
  file: Attachment | undefined;
  setIsOpen: (isOpen: boolean) => void;
  setRotateDirection: (rotateDirection: RotateDirectionType) => void;
  setGalleryFiles: (files: Attachment[]) => void;
  setSelectedFile: (file: Attachment) => void;
  onReplace?: (files: Attachment[]) => void;
  group?: string;
};

const GalleryHeader: FC<GalleryHeaderProps> = ({
  file,
  setIsOpen,
  setRotateDirection,
  setSelectedFile,
  setGalleryFiles,
  onReplace,
  group
}) => {
  const [isPreparingDownload, setIsPreparingDownload] = useState<boolean>(false);
  const [currentIndex, setCurrentIndex] = useState<number>(0);

  const { is_synced } = isFileSynced(file!);
  const rotateDirectionKeys = Object.keys(rotateDirectionEnums);
  const currentFileType = file ? getFileType(file.mime_type, file.extension).type : null;

  const isNotSynced = !is_synced;

  const onClose = () => {
    setIsOpen(false);
  };

  const resetRotateDirection = useCallback(() => {
    setCurrentIndex(0);
    setRotateDirection('top');
  }, [setRotateDirection]);

  useEffect(() => {
    resetRotateDirection();
  }, [file?.id, resetRotateDirection]);

  const onClickRotateButton = () => {
    const increasedIndex = currentIndex <= 2 ? currentIndex + 1 : 0;

    setRotateDirection(rotateDirectionKeys[increasedIndex] as RotateDirectionType);
    setCurrentIndex(increasedIndex);
  };

  const downloadFile = async () => {
    setIsPreparingDownload(true);
    await handleFileDownload(file!);
    setIsPreparingDownload(false);
  };

  return (
    <div className="gallery__header">
      <div>
        <div className="gallery__header--title">{file?.filename}</div>
        {file?.size ? (
          <div className="gallery__header--subtitle">
            <span className="gallery__header--subtitle-filetype">{currentFileType}</span>
            <div className="gallery__header--subtitle-divider" />
            <span className="gallery__header--subtitle-filesize">{fileSize(file.size)}</span>
          </div>
        ) : null}
      </div>
      <div className="d-flex  align-items-center">
        {onReplace && group ? (
          <GalleryReplaceImageButton
            group={group}
            onReplace={onReplace}
            setSelectedFile={setSelectedFile}
            setGalleryFiles={setGalleryFiles}
          />
        ) : null}
        {currentFileType === 'image' ? (
          <div
            onClick={onClickRotateButton}
            className={`gallery__header--icon gallery__header--icon-rotate cursor-pointer`}
          >
            <SvgRender src={rotateIcon} style={{ width: 19, height: 19 }} />
          </div>
        ) : null}

        <div
          data-cy="download_file"
          onClick={isPreparingDownload ? () => null : downloadFile}
          className={`gallery__header--icon gallery__header--icon-download ${
            isPreparingDownload || isNotSynced ? 'opacity-5 pe-none' : 'cursor-pointer'
          }`}
        >
          <SvgRender src={downloadIcon} style={{ width: 17, height: 18.5 }} />
        </div>
        <div
          data-cy="close_file_preview"
          onClick={onClose}
          className="gallery__header--icon gallery__header--icon-close cursor-pointer"
        >
          <SvgRender src={closeIcon} style={{ width: 12, height: 12 }} />
        </div>
      </div>
    </div>
  );
};

export default GalleryHeader;
